import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import type { ReactElement } from 'react';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function Declarations(): ReactElement {
  const navigate = useNavigate();
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  const { nextRoute, previousRoute } = useTaskNavigation();

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/declarations');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);

  const { interruptedStudy, detailedInterruptionReason, criminalConvictions, offenceDetails } = current;

  const {
    interruptedStudy: initialInterruptedStudy,
    detailedInterruptionReason: initialDetailedInterruptionReason,
    criminalConvictions: initialCriminalConvictions,
    offenceDetails: initialOffenceDetails,
  } = initial;

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.DECLARATIONS} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          {!applyConfig?.disableDeclarationInterruptionQuestion && (
            <>
              <Field
                leftLabel="Potential interruptions"
                value={interruptedStudy}
                originalValue={initialInterruptedStudy}
              />
              {(interruptedStudy || initialInterruptedStudy) && (
                <Field
                  leftLabel="Reason for interruption"
                  value={detailedInterruptionReason}
                  originalValue={initialDetailedInterruptionReason}
                />
              )}
            </>
          )}
          {!applyConfig?.disableDeclarationConvictionQuestion && (
            <>
              <Field
                leftLabel="Criminal charges"
                value={criminalConvictions}
                originalValue={initialCriminalConvictions}
              />
              {(criminalConvictions || initialCriminalConvictions) && (
                <Field leftLabel="Nature of offence(s)" value={offenceDetails} originalValue={initialOffenceDetails} />
              )}
            </>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
